"use client";

import type { Order, OrderItem } from "@/types/prisma";
import { AlertTriangle, CheckCircle2, FileText, Loader2, X, XCircle } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/lib/utils";
import { apiPost } from "@/services/api";
import { ItemVariantBadge } from "@/components/common/item-variant-badge";

type OrderWithItems = Order & { items: OrderItem[] };

const STATUS_OPTIONS = [
  "ALL",
  "PENDING",
  "CONFIRMED",
  "PAID",
  "PROCESSING",
  "PACKED",
  "SHIPPED",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "CANCELLED",
  "RETURNED"
] as const;

export const CANCELLATION_REASONS = [
  "Ordered by mistake",
  "Found a better price elsewhere",
  "Delivery takes too long",
  "Wrong product/size/color",
  "Change quantity",
  "Payment issue",
  "Changed my mind",
  "Duplicate order",
  "Other"
] as const;

function statusTone(
  status: Order["status"]
): "success" | "deal" | "prime" | "default" {
  if (status === "DELIVERED") return "success";
  if (status === "CANCELLED" || status === "RETURNED") return "deal";
  if (status === "SHIPPED" || status === "OUT_FOR_DELIVERY") return "prime";
  if (status === "CONFIRMED" || status === "PAID") return "success";
  return "default";
}

/**
 * Customers can cancel orders only when the status is PENDING, CONFIRMED, or PAID.
 * Once an order reaches PROCESSING or beyond, cancellation is prohibited.
 */
function isOrderCancelEligible(status: Order["status"]): boolean {
  return status === "PENDING" || status === "CONFIRMED" || status === "PAID";
}

export function OrdersClient({ orders: initialOrders }: { orders: OrderWithItems[] }) {
  const [orders, setOrders] = useState<OrderWithItems[]>(initialOrders);
  useEffect(() => setOrders(initialOrders), [initialOrders]);

  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_OPTIONS)[number]>("ALL");

  // Cancellation dialog state
  const [selectedOrderToCancel, setSelectedOrderToCancel] = useState<OrderWithItems | null>(null);
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [customExplanation, setCustomExplanation] = useState<string>("");
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const filtered = useMemo(
    () =>
      statusFilter === "ALL"
        ? orders
        : orders.filter((o) => o.status === statusFilter),
    [orders, statusFilter]
  );

  const openCancelModal = (order: OrderWithItems) => {
    setSelectedOrderToCancel(order);
    setSelectedReason("");
    setCustomExplanation("");
    setCancelError(null);
  };

  const closeCancelModal = () => {
    if (isCancelling) return;
    setSelectedOrderToCancel(null);
    setSelectedReason("");
    setCustomExplanation("");
    setCancelError(null);
  };

  const handleConfirmCancellation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrderToCancel) return;

    if (!selectedReason) {
      setCancelError("Please select a reason for cancellation.");
      return;
    }

    if (selectedReason === "Other" && customExplanation.trim().length < 3) {
      setCancelError("Please provide an explanation for your cancellation reason (minimum 3 characters).");
      return;
    }

    setIsCancelling(true);
    setCancelError(null);

    try {
      const res = await apiPost<{ ok: boolean; order: OrderWithItems; message: string }>(
        `/api/orders/${selectedOrderToCancel.id}/cancel`,
        {
          reason: selectedReason,
          explanation: customExplanation.trim() || undefined,
        }
      );

      if (res?.ok && res.order) {
        setOrders((prev) =>
          prev.map((o) => (o.id === res.order.id ? { ...o, ...res.order } : o))
        );
        toast.success(res.message || "Your order has been cancelled successfully.");
        closeCancelModal();
      } else {
        throw new Error("Unable to cancel order.");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unable to cancel order. Please try again.";
      setCancelError(msg);
      toast.error(msg);
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Filter bar */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar sm:flex-wrap sm:pb-0">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`shrink-0 rounded-full border px-3 py-1 text-xs font-bold transition ${
              statusFilter === s
                ? "border-amazon-orange bg-amazon-orange/10 text-amazon-orange"
                : "border-slate-200 text-slate-600 hover:border-amazon-orange dark:border-white/10 dark:text-slate-300"
            }`}
          >
            {s === "ALL" ? `All (${orders.length})` : s}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="text-sm text-slate-500">No orders match this filter.</p>
      )}

      {filtered.map((order) => {
        const canCancel = isOrderCancelEligible(order.status);
        const isCancelled = order.status === "CANCELLED";

        return (
          <article
            key={order.id}
            className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs dark:border-white/10 dark:bg-slate-900 transition hover:border-slate-300 dark:hover:border-white/20"
          >
            {/* Header summary bar */}
            <div className="flex flex-col gap-3 bg-slate-50/90 px-4 py-3.5 sm:px-5 sm:py-4 text-sm dark:bg-white/5 md:flex-row md:items-center md:justify-between border-b border-slate-200/80 dark:border-white/10">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 flex-1">
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">Order placed</p>
                  <p className="font-bold text-slate-900 dark:text-white">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">Total</p>
                  <p className="font-bold text-[#073B70] dark:text-sky-300">{formatPrice(order.total)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">Status</p>
                  <div className="mt-0.5">
                    <Badge tone={statusTone(order.status)}>{order.status}</Badge>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">Order ID</p>
                  <p className="truncate font-mono text-xs font-bold text-slate-700 dark:text-slate-300" title={order.id}>
                    {order.id}
                  </p>
                </div>
              </div>

              {/* Action buttons: Cancel Order shown only for eligible orders (PENDING / CONFIRMED / PAID) */}
              <div className="flex items-center gap-2 pt-2 md:pt-0 border-t border-slate-200/60 md:border-t-0 dark:border-white/10">
                {canCancel && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openCancelModal(order)}
                    className="h-8 rounded-lg border-rose-200 bg-rose-50/60 px-3 text-xs font-bold text-rose-600 transition hover:border-rose-300 hover:bg-rose-100/70 hover:text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-400 dark:hover:bg-rose-950/50"
                  >
                    Cancel Order
                  </Button>
                )}
              </div>
            </div>

            {/* Cancellation Banner if cancelled */}
            {isCancelled && (
              <div className="border-b border-rose-200/80 bg-rose-50/70 px-4 py-3 sm:px-5 dark:border-rose-900/40 dark:bg-rose-950/30 text-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 text-rose-800 dark:text-rose-300">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400" />
                    <span>
                      <strong className="font-bold">Order Cancelled:</strong> {order.cancelReason || "Cancelled by customer"}
                    </span>
                  </div>
                  {order.cancelledAt && (
                    <span className="text-[11px] text-rose-600 dark:text-rose-400">
                      Cancelled on {new Date(order.cancelledAt).toLocaleString()}
                    </span>
                  )}
                </div>
                {order.paymentStatus === "REFUNDED" && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 text-[11px] font-semibold">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    <span>Refund initiated to original payment method.</span>
                  </div>
                )}
              </div>
            )}

            {/* Items list */}
            <div className="divide-y divide-slate-100 dark:divide-white/10">
              {order.items.map((item) => (
                <div key={item.id} className="flex gap-4 p-4 sm:p-5">
                  <Link
                    href={item.variantId ? `/products/${item.productId}?variant=${item.variantId}` : `/products/${item.productId}`}
                    className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-slate-200/80 bg-slate-50 dark:border-white/10 dark:bg-slate-800"
                  >
                    <Image
                      src={item.image}
                      alt={item.title}
                      fill
                      sizes="80px"
                      className="object-cover"
                    />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-slate-950 dark:text-white line-clamp-2 text-sm sm:text-base">
                      {item.title}
                    </h3>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <ItemVariantBadge item={item} size="sm" showDefaultFallback={false} />
                      {item.sku && <span className="text-[11px] font-mono text-slate-400">SKU: {item.sku}</span>}
                    </div>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                      Qty {item.quantity} &bull; <span className="font-semibold text-slate-900 dark:text-white">{formatPrice(item.price)}</span>
                    </p>
                    <Link
                      href={item.variantId ? `/products/${item.productId}?variant=${item.variantId}` : `/products/${item.productId}`}
                      className="mt-2.5 inline-flex text-xs sm:text-sm font-bold text-[#087DB5] hover:text-[#073B70] dark:text-teal-400 dark:hover:text-teal-300 transition-colors"
                    >
                      Buy it again
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Shipping address footer */}
            <div className="border-t border-slate-100 bg-slate-50/40 px-4 py-2.5 sm:px-5 sm:py-3 text-xs text-slate-500 dark:border-white/10 dark:bg-slate-900/60">
              Deliver to: <span className="font-semibold text-slate-700 dark:text-slate-300">{order.shippingFullName}</span> &bull; {order.shippingLine1}, {order.shippingCity}, {order.shippingState} {order.shippingPincode}
            </div>
          </article>
        );
      })}

      {/* Cancel Order Confirmation Dialog */}
      {selectedOrderToCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs animate-in fade-in duration-200">
          <div
            className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-xl dark:border-white/10 dark:bg-slate-900 text-slate-900 dark:text-white animate-in zoom-in-95 duration-150"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-dialog-title"
          >
            {/* Close button */}
            <button
              type="button"
              onClick={closeCancelModal}
              disabled={isCancelling}
              className="absolute right-4 top-4 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-slate-200"
              aria-label="Close dialog"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Modal Header */}
            <div className="flex items-start gap-3.5">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400">
                <AlertTriangle className="h-6 w-6 stroke-[2]" />
              </div>
              <div className="min-w-0 pr-6">
                <h2 id="cancel-dialog-title" className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                  Cancel Order
                </h2>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  Order ID: <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{selectedOrderToCancel.id}</span>
                </p>
              </div>
            </div>

            {/* Refund notice if paid */}
            {selectedOrderToCancel.paymentStatus === "PAID" ? (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/80 p-3 text-xs text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300">
                <p className="font-bold flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  Refund Policy
                </p>
                <p className="mt-1 text-emerald-700 dark:text-emerald-300/90 leading-relaxed">
                  Payment has already been received for this order. Cancelling will immediately trigger a full refund of{" "}
                  <strong>{formatPrice(selectedOrderToCancel.total)}</strong> back to your original payment method.
                </p>
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-slate-200/80 bg-slate-50 p-3 text-xs text-slate-600 dark:border-white/10 dark:bg-slate-800/40 dark:text-slate-300">
                This order has not been charged yet. Cancelling will void the order and release reserved stock.
              </div>
            )}

            {cancelError && (
              <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-semibold text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-300">
                {cancelError}
              </div>
            )}

            {/* Cancellation Form */}
            <form onSubmit={handleConfirmCancellation} className="mt-4 space-y-4">
              <div>
                <label htmlFor="cancel-reason" className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                  Reason for cancellation <span className="text-rose-500">*</span>
                </label>
                <select
                  id="cancel-reason"
                  value={selectedReason}
                  onChange={(e) => {
                    setSelectedReason(e.target.value);
                    setCancelError(null);
                  }}
                  disabled={isCancelling}
                  required
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/15 dark:bg-slate-800 dark:text-white"
                >
                  <option value="" disabled>
                    -- Select a reason --
                  </option>
                  {CANCELLATION_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              {/* Text explanation */}
              {selectedReason === "Other" ? (
                <div>
                  <label htmlFor="cancel-explanation" className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                    Written Explanation <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    id="cancel-explanation"
                    rows={3}
                    value={customExplanation}
                    onChange={(e) => {
                      setCustomExplanation(e.target.value);
                      setCancelError(null);
                    }}
                    placeholder="Please specify why you are cancelling this order..."
                    disabled={isCancelling}
                    required
                    className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm text-slate-900 outline-none transition focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/15 dark:bg-slate-800 dark:text-white"
                  />
                  <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                    Required when selecting &apos;Other&apos; (min 3 characters).
                  </p>
                </div>
              ) : selectedReason ? (
                <div>
                  <label htmlFor="cancel-explanation" className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                    Additional details <span className="text-slate-400 text-[10px] lowercase">(optional)</span>
                  </label>
                  <input
                    id="cancel-explanation"
                    type="text"
                    value={customExplanation}
                    onChange={(e) => setCustomExplanation(e.target.value)}
                    placeholder="Any extra feedback or notes for our team..."
                    disabled={isCancelling}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/15 dark:bg-slate-800 dark:text-white"
                  />
                </div>
              ) : null}

              {/* Action buttons */}
              <div className="mt-6 flex flex-col-reverse sm:flex-row items-center justify-end gap-2.5 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeCancelModal}
                  disabled={isCancelling}
                  className="w-full sm:w-auto rounded-xl font-semibold text-slate-700 dark:text-slate-200"
                >
                  Keep Order
                </Button>
                <Button
                  type="submit"
                  disabled={isCancelling || !selectedReason || (selectedReason === "Other" && customExplanation.trim().length < 3)}
                  className="w-full sm:w-auto rounded-xl bg-rose-600 font-bold text-white hover:bg-rose-700 shadow-sm transition active:scale-95 disabled:opacity-50"
                >
                  {isCancelling ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    "Confirm Cancellation"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
